// pages/Home.js
import TokenomicsInfo from "../components/TokenomicsInfo";
import { useWeb3 } from "../context/Web3Context";
import { formatNumber, formatAddress, formatBalance } from "../utils/formatters";
import { useState, useEffect } from "react";

export default function Home() {
  const {
    account,
    ethBalance,
    tokenBalance,
    canMint,
    mintAmount,
    countdown,
    totalSupply,
    maxSupplyNow,
    handleMint
  } = useWeb3();

  const [isMinting, setIsMinting] = useState(false);
  const [mintProgress, setMintProgress] = useState(0);
  const [cooldownProgress, setCooldownProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Cooldown constants (24 hours = 86400 seconds)
  const TOTAL_COOLDOWN = 86400; // 24 hours in seconds

  // Handle mint with animation
  const handleMintWithAnimation = async () => {
    if (!canMint || isMinting) return;
    
    setIsMinting(true);
    setMintProgress(0);
    
    // Simulate progress animation
    const interval = setInterval(() => {
      setMintProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);
    
    try {
      await handleMint();
      
      // Complete the animation
      clearInterval(interval);
      setMintProgress(100);
      
      // Show success state briefly
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setIsMinting(false);
        setMintProgress(0);
      }, 2000);
      
    } catch (error) {
      clearInterval(interval);
      setIsMinting(false);
      setMintProgress(0);
    }
  };

  // Calculate cooldown progress from countdown string
  useEffect(() => {
    if (canMint) {
      setCooldownProgress(100); // 100% when ready
      return;
    }
    
    if (!countdown || countdown === "Now") {
      setCooldownProgress(100);
      return;
    }
    
    // Parse the countdown string (e.g., "10m 30s", "1h 30m 20s")
    try {
      let totalSecondsLeft = 0;
      
      if (countdown.includes('h')) {
        // Format: "1h 30m 20s"
        const parts = countdown.split(' ');
        parts.forEach(part => {
          if (part.includes('h')) {
            totalSecondsLeft += parseInt(part.replace('h', '')) * 3600;
          } else if (part.includes('m')) {
            totalSecondsLeft += parseInt(part.replace('m', '')) * 60;
          } else if (part.includes('s')) {
            totalSecondsLeft += parseInt(part.replace('s', ''));
          }
        });
      } else if (countdown.includes('m')) {
        // Format: "30m 20s"
        const parts = countdown.split(' ');
        parts.forEach(part => {
          if (part.includes('m')) {
            totalSecondsLeft += parseInt(part.replace('m', '')) * 60;
          } else if (part.includes('s')) {
            totalSecondsLeft += parseInt(part.replace('s', ''));
          }
        });
      }
      
      // Calculate progress percentage
      const progress = ((TOTAL_COOLDOWN - totalSecondsLeft) / TOTAL_COOLDOWN) * 100;
      setCooldownProgress(Math.min(100, Math.max(0, progress)));
      
    } catch (error) {
      console.error("Error parsing countdown:", error);
      setCooldownProgress(0);
    }
  }, [countdown, canMint]);

  return (
    <div className="home-container">
      <h1>⛏️ $MINE on DBK Chain</h1>

      

      {account && (
        <div className="info-panel">
          <p>
            <strong>Wallet:</strong> 
            <span 
              className="wallet-address" 
              title={account}
              onClick={() => navigator.clipboard.writeText(account)}
              style={{cursor: 'pointer', marginLeft: '5px'}}
            >
              {formatAddress(account)}
            </span>
            <button 
              onClick={() => navigator.clipboard.writeText(account)}
              className="copy-button"
              style={{marginLeft: '8px', padding: '2px 6px', fontSize: '10px'}}
            >
              📋
            </button>
          </p>
          
          <p><strong>ETH Balance:</strong> {formatBalance(ethBalance)} ETH</p>
          <p><strong>Token Balance:</strong> {formatNumber(tokenBalance)} MINE</p>
          
          
       

          {/* Mint Button with Animation */}
          <div style={{ position: 'relative', margin: '20px 0' }}>
            <button
              onClick={handleMintWithAnimation}
              disabled={!canMint || isMinting}
              className={`mint-button ${canMint && !isMinting ? "enabled" : "disabled"}`}
              style={{ position: 'relative', zIndex: 2 }}
            >
              {showSuccess ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  ✅ Success! <span style={{ marginLeft: '5px' }}>Refreshing...</span>
                </span>
              ) : isMinting ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div className="spinner" style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    marginRight: '8px',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Mining... ({mintProgress}%)
                </span>
              ) : canMint ? (
                `Mine ${formatNumber(mintAmount)} MINE`
              ) : (
                `Cooldown (${countdown})`
              )}
            </button>
            
            {/* Progress bar behind button for minting */}
            {isMinting && (
              <div 
                className="progress-bar" 
                style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '0', 
                  right: '0', 
                  transform: 'translateY(-50%)',
                  zIndex: 1,
                  opacity: 0.7
                }}
              >
                <div 
                  className="progress-fill"
                  style={{ 
                    width: `${mintProgress}%`,
                    background: 'linear-gradient(90deg, #4f46e5, #6366f1)',
                    transition: 'width 0.2s ease'
                  }}
                ></div>
              </div>
            )}
          </div>
          
          <p className="contract-address">
            Contract: 
            <span 
              onClick={() => navigator.clipboard.writeText("0x62CDe9Aa2159461F094e3eaCBa10F7C45C0A6B4f")}
              style={{cursor: 'pointer', marginLeft: '5px'}}
              title="0x62CDe9Aa2159461F094e3eaCBa10F7C45C0A6B4f"
            >
              {formatAddress("0x62CDe9Aa2159461F094e3eaCBa10F7C45C0A6B4f")}
            </span>
          </p>
        </div>
      )}
      <TokenomicsInfo totalSupply={totalSupply} />
    </div>
  );
}